import { WModalButton } from "@/components/button/WToolButton.tsx";
import { t_Create_Button } from "@/components/forms/CreateFormAction.tsx";
import FF from "@/components/forms/FormField.module.css";
import TF from "@/components/forms/TextField.module.css";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { generateObjectStorageKeyMutation } from "@/modules/storage/account/storageAccountApi.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { Input } from "react-aria-components";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";

interface CreateKeyInputs {
    description: string;
    read_only: boolean;
}

export interface CreateKeyModalProps {
    storageAccountUuid: string;
    isDisabled?: boolean;
}

function CreateKeyModalContent({ storageAccountUuid }: CreateKeyModalProps) {
    //#region Hooks
    const { t } = useLingui();
    const createMutation = useStandardMutation(generateObjectStorageKeyMutation);
    //#endregion

    const onSubmit: SubmitHandler<CreateKeyInputs> = async (inputs) => {
        const { description, read_only } = inputs;

        await createMutation.mutateAsync({
            body: {
                description,
                read_only,
            },
            uuid: storageAccountUuid,
        });
    };

    const form = useForm<CreateKeyInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            description: "",
            read_only: false,
        },
    });

    const { control } = form;

    return (
        <WModalForm className="w-sm" form={form} onSubmit={onSubmit} title={t`Create new`} label={t_Create_Button}>
            <p className="pb-3 color-muted">
                <Trans>
                    This key has access to all buckets under this storage account. To restrict access to specific
                    buckets, create a separate storage account.
                </Trans>
            </p>

            <Controller
                control={control}
                name="description"
                render={(props) => (
                    <WTextField {...formPropsToAriaProps(props)} headless block label={t`Key name`}>
                        <Input className={cn(FF.FormFieldInput, TF.Input)} />
                    </WTextField>
                )}
            />

            <div className="pt-3">
                <Controller
                    control={control}
                    name="read_only"
                    render={({ field }) => (
                        <WCheckbox isDisabled={field.disabled} isSelected={field.value} onChange={field.onChange}>
                            <div>
                                <Trans>Read-only</Trans>
                            </div>
                        </WCheckbox>
                    )}
                />
            </div>
        </WModalForm>
    );
}

export function CreateKeyModal({ storageAccountUuid, isDisabled }: CreateKeyModalProps) {
    const { t } = useLingui();
    return (
        <WModal
            button={<WModalButton variant="border" label={t`Create new`} isDisabled={isDisabled} icon="jp-icon-add" />}
        >
            <CreateKeyModalContent storageAccountUuid={storageAccountUuid} />
        </WModal>
    );
}
